import { apiFetch, apiFetchBlob } from "@/lib/api";

import type { CorrectiveActionSource } from "./correctiveActionsApi";

export const QUALITY_CASE_STATUSES = [
  "open",
  "investigating",
  "actions_open",
  "pending_effectiveness",
  "closed",
  "cancelled",
] as const;

export type QualityCaseStatus = (typeof QUALITY_CASE_STATUSES)[number];

export const INVESTIGATION_STATUSES = ["open", "closed"] as const;

export type InvestigationStatus = (typeof INVESTIGATION_STATUSES)[number];

export interface QualityCase {
  id: number;
  title: string;
  status: QualityCaseStatus;
  sourceType: CorrectiveActionSource;
  openedAt: string;
  closedAt: string | null;
  reportedBy: string;
  reportedByDisplay: string | null;
  ownerUserId: string | null;
  ownerDisplay: string | null;
  clientFsAccountId: number | null;
  clientName: string | null;
  salesOrderId: number | null;
  salesOrderReference: string | null;
  purchaseOrderId: number | null;
  purchaseOrderReference: string | null;
  productFamily: string | null;
  closedBy: string | null;
  closedByDisplay: string | null;
  createdAt: string;
  updatedAt: string;
  incidentCount: number;
  openInvestigationId: number | null;
}

export interface InvestigationComment {
  id: number;
  investigationId: number;
  body: string;
  createdByUserId: string | null;
  createdByDisplay: string | null;
  createdAt: string;
}

export interface Investigation {
  id: number;
  qualityCaseId: number;
  status: InvestigationStatus;
  finalReport: string | null;
  comments: InvestigationComment[];
  openedAt: string;
  openedBy: string;
  openedByDisplay: string | null;
  closedAt: string | null;
  closedBy: string | null;
  closedByDisplay: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LinkedIncident {
  id: number;
  issue: string | null;
  issueDate: string | null;
  type: string | null;
  repairScrap: string | null;
  resolutionDate: string | null;
  serialFciId: number | null;
  clampSerial: string | null;
  pcbaSerial: string | null;
  deviceLabelSerial: string | null;
  open: boolean;
  rcaId: number | null;
}

export interface LinkedSalesOrder {
  id: number;
  reference: string | null;
}

export interface LinkedPurchaseOrder {
  id: number;
  reference: string | null;
}

export interface LinkedRma {
  id: number;
  rmaNumber: string;
}

export interface LinkedProductFamily {
  key: string;
  label: string;
}

export interface LinkedCorrectiveAction {
  id: number;
  carNumber: string;
  title: string;
  status: string;
}

export interface QualityCaseListResponse {
  rows: QualityCase[];
  totalCount: number;
  limit: number;
  offset: number;
}

export interface ContainmentNoticeRecipient {
  userId: string;
  displayName: string | null;
  email: string | null;
  acknowledgedAt: string | null;
  lastNotifiedAt: string | null;
}

export interface ContainmentNotice {
  id: number;
  qualityCaseId: number;
  body: string;
  createdByUserId: string;
  createdByDisplay: string | null;
  broadcastAt: string;
  recipients: ContainmentNoticeRecipient[];
}

export interface QualityCaseDetailResponse {
  case: QualityCase;
  investigations: Investigation[];
  incidents: LinkedIncident[];
  salesOrders: LinkedSalesOrder[];
  purchaseOrders: LinkedPurchaseOrder[];
  rmas: LinkedRma[];
  productFamilies: LinkedProductFamily[];
  correctiveActions: LinkedCorrectiveAction[];
  containmentNotices: ContainmentNotice[];
}

export const QUALITY_CASES_LIST_KEY = ["quality", "cases"] as const;

export function qualityCaseQueryKey(id: number) {
  return ["quality", "case", id] as const;
}

export function qualityCaseLabel(id: number): string {
  return `Quality case ${id}`;
}

export async function fetchQualityCasePdf(
  caseId: number,
): Promise<{ filename: string; pdfBase64: string; correctAsOf: string }> {
  return apiFetch<{ filename: string; pdfBase64: string; correctAsOf: string }>(
    `/api/quality/cases/${String(caseId)}/pdf`,
  );
}

export function qualityCaseStatusLabel(status: QualityCaseStatus): string {
  switch (status) {
    case "open":
      return "Open";
    case "investigating":
      return "Investigating";
    case "actions_open":
      return "Actions open";
    case "pending_effectiveness":
      return "Pending effectiveness";
    case "closed":
      return "Closed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export function investigationStatusLabel(status: InvestigationStatus): string {
  switch (status) {
    case "open":
      return "Open";
    case "closed":
      return "Closed";
    default:
      return status;
  }
}

export interface QualityCaseListFilters {
  status?: QualityCaseStatus;
  sourceType?: CorrectiveActionSource;
  search?: string;
  limit?: number;
  offset?: number;
}

function buildListQuery(filters: QualityCaseListFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.sourceType) params.set("sourceType", filters.sourceType);
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.limit != null) params.set("limit", String(filters.limit));
  if (filters.offset != null) params.set("offset", String(filters.offset));
  const query = params.toString();
  return query.length > 0 ? `?${query}` : "";
}

export async function fetchQualityCases(
  filters: QualityCaseListFilters = {},
): Promise<QualityCaseListResponse> {
  return apiFetch<QualityCaseListResponse>(`/api/quality/cases${buildListQuery(filters)}`);
}

export const QUALITY_OWNERS_QUERY_KEY = ["quality", "owners"] as const;

export type QualityOwnerOption = {
  id: string;
  display_name: string;
};

export async function fetchQualityOwners(): Promise<{ users: QualityOwnerOption[] }> {
  return apiFetch<{ users: QualityOwnerOption[] }>("/api/quality/owners");
}

export type QualitySalesOrderSearchRow = {
  id: number;
  number: string | null;
  status: string | null;
  client_name: string | null;
  sgs_ship_by_date: string | null;
  customer_purchase_order_number: string | null;
};

export async function fetchQualitySalesOrderQuickSearch(
  query: string,
): Promise<{ rows: QualitySalesOrderSearchRow[] }> {
  return apiFetch<{ rows: QualitySalesOrderSearchRow[] }>("/api/quality/sales-orders/quick-search", {
    query: { q: query, limit: "20" },
  });
}

export type QualityPurchaseOrderSearchRow = {
  id: number;
  number: string | null;
  status: string | null;
  supplier_name: string | null;
};

export async function fetchQualityPurchaseOrderQuickSearch(
  query: string,
): Promise<{ rows: QualityPurchaseOrderSearchRow[] }> {
  return apiFetch<{ rows: QualityPurchaseOrderSearchRow[] }>("/api/quality/purchase-orders/quick-search", {
    query: { q: query, limit: "20" },
  });
}

export async function fetchQualityCaseDetail(id: number): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(`/api/quality/cases/${id}`);
}

export interface CreateQualityCaseInput {
  title: string;
  sourceType?: CorrectiveActionSource;
  ownerUserId?: string | null;
  salesOrderIds?: number[];
  purchaseOrderIds?: number[];
  rmaIds?: number[];
  qualityIncidentIds?: number[];
  productFamilies?: string[];
}

export async function createQualityCase(input: CreateQualityCaseInput): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>("/api/quality/cases", {
    method: "POST",
    body: (input),
  });
}

export type UpdateQualityCaseInput = Partial<{
  title: string;
  status: QualityCaseStatus;
  sourceType: CorrectiveActionSource;
  ownerUserId: string | null;
  clientFsAccountId: number | null;
  salesOrderIds: number[];
  purchaseOrderIds: number[];
  rmaIds: number[];
  productFamilies: string[];
}>;

export async function updateQualityCase(id: number, input: UpdateQualityCaseInput): Promise<QualityCase> {
  return apiFetch<QualityCase>(`/api/quality/cases/${id}`, {
    method: "PATCH",
    body: (input),
  });
}

export async function startQualityInvestigation(caseId: number): Promise<Investigation> {
  return apiFetch<Investigation>(`/api/quality/cases/${caseId}/investigations`, {
    method: "POST",
    body: ({}),
  });
}

export type UpdateInvestigationInput = Partial<{
  status: InvestigationStatus;
  finalReport: string | null;
}>;

export async function updateQualityInvestigation(
  caseId: number,
  investigationId: number,
  input: UpdateInvestigationInput,
): Promise<Investigation> {
  return apiFetch<Investigation>(`/api/quality/cases/${caseId}/investigations/${investigationId}`, {
    method: "PATCH",
    body: (input),
  });
}

export async function addQualityInvestigationComment(
  caseId: number,
  investigationId: number,
  body: string,
): Promise<Investigation> {
  return apiFetch<Investigation>(`/api/quality/cases/${caseId}/investigations/${investigationId}/comments`, {
    method: "POST",
    body: ({ body }),
  });
}

export type QualityIncidentSearchRow = {
  id: number;
  issue: string | null;
  issueDate: string | null;
  type: string | null;
  pcbaSerial: string | null;
  deviceLabelSerial: string | null;
  mcuUuid: string | null;
  qualityCaseId: number | null;
  open: boolean;
};

export async function fetchQualityIncidentQuickSearch(
  query: string,
): Promise<{ rows: QualityIncidentSearchRow[] }> {
  return apiFetch<{ rows: QualityIncidentSearchRow[] }>("/api/quality/incidents/quick-search", {
    query: { q: query },
  });
}

export async function linkQualityCaseIncident(
  caseId: number,
  qualityIncidentId: number,
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(`/api/quality/cases/${caseId}/incidents`, {
    method: "POST",
    body: ({ qualityIncidentId }),
  });
}

export async function unlinkQualityCaseIncident(
  caseId: number,
  incidentId: number,
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(`/api/quality/cases/${caseId}/incidents/${incidentId}`, {
    method: "DELETE",
  });
}

export async function linkQualityCaseRma(
  caseId: number,
  rmaId: number,
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(`/api/quality/cases/${caseId}/rmas`, {
    method: "POST",
    body: ({ rmaId }),
  });
}

export async function unlinkQualityCaseRma(
  caseId: number,
  rmaId: number,
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(`/api/quality/cases/${caseId}/rmas/${rmaId}`, {
    method: "DELETE",
  });
}

export async function linkQualityCaseCorrectiveAction(
  caseId: number,
  correctiveActionReportId: number,
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(`/api/quality/cases/${caseId}/corrective-actions`, {
    method: "POST",
    body: ({ correctiveActionReportId }),
  });
}

export async function unlinkQualityCaseCorrectiveAction(
  caseId: number,
  reportId: number,
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(`/api/quality/cases/${caseId}/corrective-actions/${reportId}`, {
    method: "DELETE",
  });
}

export type QualityContainmentAudienceUser = {
  id: string;
  display_name: string | null;
  email: string;
};

export async function fetchQualityContainmentAudience(): Promise<{ users: QualityContainmentAudienceUser[] }> {
  return apiFetch<{ users: QualityContainmentAudienceUser[] }>("/api/quality/containment-audience");
}

export async function broadcastQualityContainmentNotice(
  caseId: number,
  input: { body: string; recipientUserIds: string[] },
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(`/api/quality/cases/${caseId}/containment-notices`, {
    method: "POST",
    body: (input),
  });
}

export async function acknowledgeQualityContainmentNotice(
  caseId: number,
  noticeId: number,
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(
    `/api/quality/cases/${caseId}/containment-notices/${noticeId}/acknowledge`,
    { method: "POST" },
  );
}

export async function addQualityContainmentRecipients(
  caseId: number,
  noticeId: number,
  recipientUserIds: string[],
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(
    `/api/quality/cases/${caseId}/containment-notices/${noticeId}/recipients`,
    {
      method: "POST",
      body: ({ recipientUserIds }),
    },
  );
}

export async function fetchQualityCaseTravelerHtml(
  caseId: number,
  investigationId?: number | null,
): Promise<string> {
  const query =
    investigationId != null && Number.isFinite(investigationId) && investigationId > 0
      ? `?investigationId=${String(investigationId)}`
      : "";
  const { blob } = await apiFetchBlob(`/api/quality/cases/${String(caseId)}/traveler.html${query}`);
  return blob.text();
}

export async function fetchQualityCaseTravelerPdf(
  caseId: number,
  investigationId?: number | null,
): Promise<{ filename: string; pageCount: number; pdfBase64: string }> {
  const query =
    investigationId != null && Number.isFinite(investigationId) && investigationId > 0
      ? `?investigationId=${String(investigationId)}`
      : "";
  return apiFetch<{ filename: string; pageCount: number; pdfBase64: string }>(
    `/api/quality/cases/${String(caseId)}/traveler.pdf${query}`,
  );
}

export async function printQualityCaseTraveler(
  caseId: number,
  input: { printer: string; investigationId?: number | null; copies?: number },
): Promise<{ success: true; printer: string; copies: number; filename: string; pageCount: number }> {
  return apiFetch(`/api/quality/cases/${String(caseId)}/traveler/print`, {
    method: "POST",
    body: ({
      printer: input.printer,
      investigationId: input.investigationId ?? undefined,
      copies: input.copies ?? 1,
    }),
  });
}

export interface QualityCaseMessage {
  id: string;
  createdAt: string;
  authorUserId: string;
  authorEmail: string;
  authorDisplayName: string;
  body: string;
}

export async function fetchQualityCaseMessages(caseId: number): Promise<{ messages: QualityCaseMessage[] }> {
  return apiFetch<{ messages: QualityCaseMessage[] }>(`/api/quality/cases/${caseId}/messages`);
}

export async function postQualityCaseMessage(
  caseId: number,
  body: string,
): Promise<{ messages: QualityCaseMessage[] }> {
  return apiFetch<{ messages: QualityCaseMessage[] }>(`/api/quality/cases/${caseId}/messages`, {
    method: "POST",
    body: ({ body }),
  });
}

export interface QualityCaseActivityItem {
  occurredAt: string;
  recordType: string;
  recordId: number;
  recordLabel: string;
  href: string;
  text: string;
}

export async function fetchQualityCaseActivity(
  caseId: number,
): Promise<{ rows: QualityCaseActivityItem[] }> {
  return apiFetch<{ rows: QualityCaseActivityItem[] }>(`/api/quality/cases/${caseId}/activity`);
}

export async function createQualityCaseEvent(
  caseId: number,
  input: {
    sourceCategory: string;
    summary?: string | null;
    supplierId?: number | null;
    customerFsAccountId?: number | null;
  },
): Promise<{ id: number; qualityCaseId: number }> {
  return apiFetch(`/api/quality/cases/${caseId}/events`, {
    method: "POST",
    body: (input),
  });
}

export async function linkQualityCaseEvent(
  caseId: number,
  qualityEventId: number,
): Promise<{ id: number; qualityCaseId: number }> {
  return apiFetch(`/api/quality/cases/${caseId}/events/link`, {
    method: "POST",
    body: ({ qualityEventId }),
  });
}

export async function resendQualityContainmentNotice(
  caseId: number,
  noticeId: number,
  input: { pendingOnly: true } | { recipientUserIds: string[] },
): Promise<QualityCaseDetailResponse> {
  return apiFetch<QualityCaseDetailResponse>(
    `/api/quality/cases/${caseId}/containment-notices/${noticeId}/resend`,
    {
      method: "POST",
      body: (input),
    },
  );
}
